/**
 * Reading an untrusted message part.
 *
 * Split out of `../messageTimeline.ts`. Parts reach the renderer from the live
 * stream AND from a database row written by an older version of the app, so
 * nothing here assumes a shape — every field is checked before it is read.
 *
 * The two shapes that matter: a STREAMING tool part is typed `tool-find_company`
 * and a PERSISTED one is typed `tool-call` with `toolName: 'find_company'`.
 * Confusing them is not a crash, it is a reload that quietly renames every tool
 * to "call" and loses its plan cards and group labels.
 */

import type { ToolPartWithState } from '../toolInvocations.js';

/**
 * Canonical (persisted) tool part types. These carry the real tool name on
 * `toolName`; the streaming form encodes it in the type as `tool-<name>`.
 */
const CANONICAL_TOOL_TYPES = new Set([
  'tool-call',
  'tool-result',
  'tool-approval-request',
  'tool-approval-response',
]);

export const TOOL_TYPE_PREFIX = 'tool-';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Every field of `ToolPartWithState` is optional by design — parts arrive from
 * the stream/DB untrusted — so any record with a part type satisfies the shape.
 * Passing the ORIGINAL object through matters: ToolBlock reads `output`,
 * `errorText`, and `approval` off it, which a reconstructed copy would drop.
 */
export function isToolPartLike(value: unknown): value is ToolPartWithState {
  return isRecord(value) && typeof value.type === 'string';
}

/**
 * Resolve a tool's name from either part shape.
 *
 * Streaming parts are typed `tool-find_company`; persisted parts are typed
 * `tool-call` with `toolName: 'find_company'` (see `lib/db/chat-persistence`).
 * Stripping the prefix first would turn every persisted part into the tool
 * named "call" — which silently loses plan cards and group labels on reload.
 */
export function resolveToolName(part: ToolPartWithState): string {
  const type = part.type ?? '';
  if (CANONICAL_TOOL_TYPES.has(type)) {
    return part.toolName ?? '';
  }
  if (type.startsWith(TOOL_TYPE_PREFIX)) {
    return type.slice(TOOL_TYPE_PREFIX.length);
  }
  return part.toolName ?? '';
}

/**
 * An approval is OUTSTANDING when the part asks for one and it has not been
 * granted yet. Matches the classification the chat UI has always used.
 */
export function resolveOutstandingApprovalId(part: ToolPartWithState): string | null {
  const approvalId =
    part.approval?.id ?? (isRecord(part) ? optionalString(part.approvalId) : undefined);
  if (!approvalId) return null;
  const asksForApproval = part.type === 'tool-approval-request' || Boolean(part.approval?.id);
  if (!asksForApproval) return null;
  if (part.approval?.approved) return null;
  return approvalId;
}
